import { useMemo } from 'react';
import { Group } from '@visx/group';
import { scaleBand, scaleLinear } from '@visx/scale';
import { getStringWidth } from '@visx/text';
import { SVGMeasure, useSVGMeasure } from '@tether-web-portal/components/charts/SVGMeasure';
import { ColumnLabels } from '@tether-web-portal/components/heat-map/v2/ColumnLabels';
import { RowLabels } from '@tether-web-portal/components/heat-map/v2/RowLabels';
import { Cells } from "@tether-web-portal/components/heat-map/v2/Cells";
import { useInteractionRect } from "@tether-web-portal/components/heat-map/v2/useInteractionRect";
import { HeatMapTooltip } from "@tether-web-portal/components/heat-map/v2/tooltip/HeatMapTooltip";
import { ScaleBand, ScaleLinear } from "d3";
import { ColumnAxisBottomLabel } from "@tether-web-portal/components/heat-map/v2/ColumnAxisBottomLabel";
import { ColumnBackground } from "@tether-web-portal/components/heat-map/v2/ColumnBackground";
import { AxisConfig, TooltipDisplayComponent } from "@tether-web-portal/components/charts/types";

export type HeatMapDataPoint<T = unknown> = {
  row: number;
  column: number;
  value?: number;
  data?: T;
  faded?: boolean;
};

export type HeatMapColumn = {
  key: number;
  label: string;
  timestamp: number;
  faded?: boolean;
};

export type HeatMapRow = {
  key: number;
  label: string;
  timestamp: number;
  benchmark?: {
    start: boolean;
    end: boolean;
  };
};

export type HeatMapXScale = ScaleBand<number>;
export type HeatMapYScale = ScaleBand<number>;
export type HeatMapColorScale = ScaleLinear<string, string, never>;
export type HeatMapDataMap<T> = Record<number, Record<number, HeatMapDataPoint<T>>>;

interface HeatMapProps<T> {
  columns: HeatMapColumn[];
  rows: HeatMapRow[];
  data: HeatMapDataPoint<T>[];
  gap?: number;
  maxValue?: number;
  colorScale: HeatMapColorScale;
  rowAxisLabel: string;
  columnsAxisLabel: string;
  TooltipDisplay?: TooltipDisplayComponent<T>;
}

const labelLineHeight = 14;

const defaultXAxisHeightConfig = new AxisConfig({
  lineHeight: labelLineHeight,
  title: labelLineHeight,
  gap: 24,
  label: labelLineHeight,
  outerMargin: 10,
});

const defaultYAxisWidthConfig = new AxisConfig({
  lineHeight: labelLineHeight,
  title: 20,
  label: 100,
});

const rowLabelStyle = {
  lineHeight: `${labelLineHeight}px`,
  fontSize: "var(--chakra-fontSizes-xs)",
  fontWeight: 700,
  fontFamily: "var(--chakra-fonts-body)",
};

const columnLabelStyle = {
  lineHeight: `${labelLineHeight}px`,
  fontSize: "var(--chakra-fontSizes-xs)",
  fontWeight: 500,
  fontFamily: "var(--chakra-fonts-body)",
};

const HeatMapContents = <T,>({
  data,
  columns,
  rows,
  gap = 5,
  colorScale,
  rowAxisLabel,
  columnsAxisLabel,
  TooltipDisplay,
  maxValue,
}: HeatMapProps<T>) => {
  const { width } = useSVGMeasure();

  const yAxisWidthConfig = useMemo(() => {
    const longest = Math.max(...rows.map(({ label }) => getStringWidth(label, rowLabelStyle) || 0));
    const label = Math.min(longest, defaultYAxisWidthConfig.label);

    const hasBenchmarks = rows.some(({ benchmark }) => benchmark?.start || benchmark?.end);
    const { ...settings } = defaultYAxisWidthConfig;
    return new AxisConfig({
      ...settings,
      gap: hasBenchmarks ? 10 : 0,
      label,
    });
  }, [rows]);

  const dataMap = useMemo<HeatMapDataMap<T>>(() => {
    const dataMap: HeatMapDataMap<T> = {};
    for (const item of data) {
      const { row, column } = item;
      if (!dataMap[column]) dataMap[column] = {};
      dataMap[column][row] = item;
    }
    return dataMap;
  }, [data]);

  const minColumnWidth = 34;

  const rowHeight = 22;

  const xMax = width - yAxisWidthConfig.total;
  const yMax = rows.length * (rowHeight + gap) - gap;

  const columnTopLabelHeight = defaultXAxisHeightConfig.outerMargin + defaultXAxisHeightConfig.label;
  const height = yMax + defaultXAxisHeightConfig.total;

  const columnXMax = columns.length * (minColumnWidth + gap) - gap;

  const xScale = useMemo(
    (): HeatMapXScale =>
      scaleBand({
        range: [0, columnXMax],
        domain: columns.map(({ key }) => key),
        padding: gap / minColumnWidth,
      }),
    [columns, columnXMax, minColumnWidth, gap],
  );

  const yScale = useMemo((): HeatMapYScale => {
    return scaleBand({
      range: [0, yMax],
      domain: rows.map(({ key }) => key),
      padding: gap / rowHeight,
    });
  }, [gap, rows, yMax]);

  const [rectProps, { hoveredCell, scrollOffsetX, scrollOffsetY }] = useInteractionRect<T>({
    xMax,
    yMax,
    xScale,
    yScale,
    dataMap,
  });

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      version="1.1"
      xmlns="http://www.w3.org/2000/svg"
    >
      <Group left={yAxisWidthConfig.total}>
        <Group left={scrollOffsetX}>
          <ColumnBackground
            columns={columns}
            xScale={xScale}
            columnXMax={columnXMax}
            xMax={xMax}
            backgroundHeight={columnTopLabelHeight + yMax}
          />
        </Group>
      </Group>

      <Group top={columnTopLabelHeight} left={yAxisWidthConfig.total}>
        <Group left={scrollOffsetX}>
          <Cells<T>
            columns={columns}
            rows={rows}
            dataMap={dataMap}
            xScale={xScale}
            yScale={yScale}
            colorScale={colorScale}
          />

          <HeatMapTooltip<T>
            data={hoveredCell}
            xScale={xScale}
            yScale={yScale}
            colorScale={colorScale}
            TooltipDisplay={TooltipDisplay}
            maxValue={maxValue}
          />
        </Group>
      </Group>

      <Group left={yAxisWidthConfig.total}>
        <Group left={scrollOffsetX}>
          <ColumnLabels
            columns={columns}
            xScale={xScale}
            axisHeightConfig={defaultXAxisHeightConfig}
            labelStyle={columnLabelStyle}
          />
        </Group>

        <ColumnAxisBottomLabel
          xMax={xMax}
          backgroundHeight={columnTopLabelHeight + yMax}
          labelStyle={columnLabelStyle}
          axisHeightConfig={defaultXAxisHeightConfig}
        >
          {columnsAxisLabel}
        </ColumnAxisBottomLabel>
      </Group>

      <Group top={columnTopLabelHeight}>
        <RowLabels
          rows={rows}
          yScale={yScale}
          axisWidthConfig={yAxisWidthConfig}
          axisLabel={rowAxisLabel}
          columnXMax={columnXMax}
          yMax={yMax}
          labelStyle={rowLabelStyle}
        />
      </Group>

      <rect
        data-name={"hides-overflow"}
        width={yAxisWidthConfig.total + 2}
        height={columnTopLabelHeight + 2}
        fill={"white"}
      />

      <Group top={columnTopLabelHeight} left={yAxisWidthConfig.total}>
        <rect fill={"transparent"} width={xMax} height={yMax} {...rectProps} />
      </Group>
    </svg>
  );
};

export const HeatMap = <T,>(props: HeatMapProps<T>) => (
  <SVGMeasure measureWidth >
    <HeatMapContents<T> {...props} />
  </SVGMeasure>
)
